import { usePerformanceStore } from '@/stores/performanceStore';
import { useSceneSettings } from '@/contexts/SceneSettingsContext';
import { Zap } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { FpsSparklineGraph, WorkerSparklineGraph } from './SparklineGraph';

export function PerformanceDisplay() {
  const fps = usePerformanceStore((state) => state.fps);
  const triangles = usePerformanceStore((state) => state.triangles);
  const fpsHistory = usePerformanceStore((state) => state.fpsHistory);
  const compileWorkers = usePerformanceStore((state) => state.compileWorkers);
  const textureWorkers = usePerformanceStore((state) => state.textureWorkers);
  const workerHistory = usePerformanceStore((state) => state.workerHistory);
  const modelColor = usePerformanceStore((state) => state.modelColor);
  const { performanceMode, togglePerformanceMode } = useSceneSettings();

  const totalWorkers = compileWorkers.total + textureWorkers.total;
  const activeWorkers = compileWorkers.active + textureWorkers.active;

  return (
    <div className="mr-3 flex items-center gap-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={togglePerformanceMode}
            >
              <Zap
                className={`h-4 w-4 ${
                  performanceMode
                    ? 'fill-yellow-500 text-yellow-500'
                    : 'text-muted-foreground'
                }`}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {performanceMode
              ? 'Disable Performance Mode'
              : 'Enable Performance Mode (reduces quality)'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {(fps > 0 || triangles > 0 || totalWorkers > 0) && (
        <div className="flex cursor-help select-none items-center gap-3 text-[10px] font-medium text-muted-foreground/60">
          {(fps > 0 || triangles > 0) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="transition-colors hover:text-foreground/80">
                  {fps} FPS
                </span>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="border-none bg-transparent p-0 shadow-none"
                sideOffset={6}
              >
                <div className="flex flex-col gap-1 rounded-md bg-black/80 p-2 backdrop-blur-sm">
                  <span className="text-[10px] font-semibold text-white/50">
                    FPS — last 10 seconds
                  </span>
                  <FpsSparklineGraph
                    data={fpsHistory}
                    width={160}
                    height={44}
                    maxFps={120}
                  />
                </div>
              </TooltipContent>
            </Tooltip>
          )}

          {totalWorkers > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-[3px]">
                  {Array.from({ length: totalWorkers }, (_, i) => (
                    <span
                      key={i}
                      className={`block h-[7px] w-[7px] rounded-full transition-colors ${
                        i < activeWorkers
                          ? 'bg-muted-foreground/60'
                          : 'border border-muted-foreground/40'
                      }`}
                    />
                  ))}
                </div>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="border-none bg-transparent p-0 shadow-none"
                sideOffset={6}
              >
                <div className="flex flex-col gap-1 rounded-md bg-black/80 p-2 backdrop-blur-sm">
                  <span className="text-[10px] font-semibold text-white/50">
                    Web workers — last 10 seconds
                  </span>
                  <WorkerSparklineGraph
                    data={workerHistory}
                    width={160}
                    height={44}
                    modelColor={modelColor}
                  />
                  <div className="flex items-center gap-3 text-[9px] text-white/40">
                    <span className="flex items-center gap-1">
                      <span
                        className="inline-block h-[6px] w-[6px] rounded-full"
                        style={{
                          background: modelColor
                            ? `rgba(${Math.round(modelColor[0] * 255)},${Math.round(modelColor[1] * 255)},${Math.round(modelColor[2] * 255)},0.9)`
                            : 'rgba(100,160,255,0.9)',
                        }}
                      />
                      active
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="inline-block h-[6px] w-[6px] rounded-full border border-white/25" />
                      total
                    </span>
                    <span className="ml-auto">
                      {activeWorkers}/{totalWorkers}
                    </span>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
}
